import React, { useState, useEffect } from 'react';
import API_URL from '../config';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ClientForm from './ClientForm';
import SharedNav from './SharedNav';
import StatusLabel from './shared/StatusLabel';
import './MyClients.css';

// Same client pipeline stage vocabulary as Dashboard.js -- mapped explicitly since it has
// nothing to do with candidate screening status.
const CLIENT_STATUS_TIER = { lead: 'info', closed: 'pass', onboard: 'warn', nrlv: 'fail' };

function MyClients({ user, onLogout }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCalled, setFilterCalled] = useState('all'); // all, today, notToday

  useEffect(() => {
    fetchMyClients();
  }, []);

  const fetchMyClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/clients/my-clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setLoading(false);
    }
  };

  const handleMarkAsCalled = async (clientId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/clients/${clientId}/mark-called`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchMyClients();
    } catch (err) {
      alert('Error marking client as called');
    }
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingClient(null);
    fetchMyClients();
  };

  const isCalledToday = (lastCalledDate) => {
    if (!lastCalledDate) return false;
    const today = new Date().toISOString().split('T')[0];
    return lastCalledDate === today;
  };

  const getDaysSinceLastCall = (lastCalledDate) => {
    if (!lastCalledDate) return null;
    const today = new Date();
    const lastCall = new Date(lastCalledDate);
    const diffTime = Math.abs(today - lastCall);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Filter clients
  let filteredClients = clients.filter(client => {
    // Search filter
    const search = searchTerm.toLowerCase();
    const matchesSearch = !search || (
      client.name?.toLowerCase().includes(search) ||
      client.company?.toLowerCase().includes(search) ||
      client.state?.toLowerCase().includes(search) ||
      client.city?.toLowerCase().includes(search)
    );

    if (!matchesSearch) return false;

    // Called today filter
    if (filterCalled === 'today' && !isCalledToday(client.last_called_date)) {
      return false;
    }
    if (filterCalled === 'notToday' && isCalledToday(client.last_called_date)) {
      return false;
    }

    return true;
  });

  const todayCount = clients.filter(c => isCalledToday(c.last_called_date)).length;
  const remainingCount = clients.filter(c => !isCalledToday(c.last_called_date)).length;

  return (
    <div className="my-clients">
      <SharedNav user={user} onLogout={onLogout} />

      <div className="dashboard-content">
        <div className="call-stats">
          <div className="stat-card">
            <div className="stat-number">{clients.length}</div>
            <div className="stat-label">Total Assigned</div>
          </div>
          <div className="stat-card success">
            <div className="stat-number">{todayCount}</div>
            <div className="stat-label">Called Today</div>
          </div>
          <div className="stat-card pending">
            <div className="stat-number">{remainingCount}</div>
            <div className="stat-label">To Call</div>
          </div>
        </div>

        <div className="controls">
          <input
            type="text"
            className="search-box"
            placeholder="Search by name, company, state, or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filterCalled === 'all' ? 'active' : ''}`}
              onClick={() => setFilterCalled('all')}
            >
              All ({clients.length})
            </button>
            <button
              className={`filter-btn ${filterCalled === 'notToday' ? 'active' : ''}`}
              onClick={() => setFilterCalled('notToday')}
            >
              To Call ({remainingCount})
            </button>
            <button
              className={`filter-btn ${filterCalled === 'today' ? 'active' : ''}`}
              onClick={() => setFilterCalled('today')}
            >
              Called Today ({todayCount})
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading your clients...</div>
        ) : clients.length === 0 ? (
          <div className="empty-state">
            <h3>No Clients Assigned</h3>
            <p>You don't have any clients assigned to you yet.</p>
            <p>Ask your admin to assign some clients to you.</p>
          </div>
        ) : (
          <div className="clients-grid">
            {filteredClients.map((client) => {
              const calledToday = isCalledToday(client.last_called_date);
              const daysSince = getDaysSinceLastCall(client.last_called_date);

              return (
                <div key={client.id} className={`client-card ${calledToday ? 'called-today' : ''}`}>
                  <div className="card-header">
                    <h3>{client.name}</h3>
                    {calledToday && <span className="badge-called">✓ Called Today</span>}
                  </div>

                  <div className="card-body">
                    <div className="info-row">
                      <span className="label">Company:</span>
                      <span className="value">{client.company || 'N/A'}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Location:</span>
                      <span className="value">{client.city && client.state ? `${client.city}, ${client.state}` : client.location || 'N/A'}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Status:</span>
                      <StatusLabel value={client.status || 'N/A'} tier={CLIENT_STATUS_TIER[client.status?.toLowerCase()] || 'neutral'} />
                    </div>
                    <div className="info-row">
                      <span className="label">Service:</span>
                      <span className="value">{client.service_type || 'N/A'}</span>
                    </div>
                    {client.current_provider && (
                      <div className="info-row">
                        <span className="label">Provider:</span>
                        <span className="value">{client.current_provider}</span>
                      </div>
                    )}
                    {client.last_called_date && (
                      <div className="info-row">
                        <span className="label">Last Called:</span>
                        <span className="value last-called">
                          {client.last_called_date}
                          {daysSince > 0 && !calledToday && ` (${daysSince} days ago)`}
                        </span>
                      </div>
                    )}
                    {client.call_notes && (
                      <div className="info-row notes">
                        <span className="label">Notes:</span>
                        <span className="value">{client.call_notes}</span>
                      </div>
                    )}
                  </div>

                  <div className="card-actions">
                    <button
                      onClick={() => handleEditClient(client)}
                      className="btn-secondary btn-full"
                    >
                      📝 View/Edit Details
                    </button>
                    {!calledToday && (
                      <button
                        onClick={() => handleMarkAsCalled(client.id)}
                        className="btn-primary btn-full"
                      >
                        ✓ Mark as Called
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {filteredClients.length === 0 && clients.length > 0 && (
          <div className="empty-state">
            <p>No clients match your filters.</p>
          </div>
        )}
      </div>

      {showForm && (
        <ClientForm
          client={editingClient}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
}

export default MyClients;
